import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { IconResetCircle } from "../../../common/icons/IconResetCircle/IconResetCircle";
import { useAppSelector } from "../../../store";
import defaultContent from "../../../content/earningsCalculator";
import LeafLinkButton from "../../../common/leaf/LeafLinkButton/LeafLinkButton";
import { updatePersonalPV, updateVCSPV } from "../../../reducers/earningCalculator";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { resetValuesTrack } from "../../../common/tealium/EarningCalculator/EarningCalculatorTrack";
import EarningsCalculatorInput from "./EarningsCalculatorInput/EarningsCalculatorInput";
import "./EarningsCalculatorProgressTile.scss";

type Props = {
  isCSIActive: boolean;
  isLoading: boolean;
  disableInput?: boolean;
};

const EarningsCalculatorProgressTile = ({ isCSIActive, isLoading, disableInput = false }: Props) => {
  const { t } = useTranslation(["earningsCalculator"]);
  const dispatch = useDispatch();
  const { EarningSummary, ECComputedData } = useAppSelector((state: any) => state.earningCalculator);
  const { VCSPV, PersonalPV } = EarningSummary;
  const { Settings, StaticData } = ECComputedData;
  const { VCSPV: CurrentVCSPV, PersonalPV: CurrentPersonalPV } = StaticData;
  const isDisabled =
    (CurrentPersonalPV.Actual === Settings.PersonalPV.Max && CurrentVCSPV.Actual === Settings.CSI.GPV.Max) ||
    disableInput;

  const handleReset = () => {
    if (isDisabled) return;

    resetValuesTrack();
    dispatch(updatePersonalPV(CurrentPersonalPV.Actual));
    dispatch(updateVCSPV(CurrentVCSPV.Actual));
  };

  const updateValuePersonalPV = (value: number | "") => {
    dispatch(updatePersonalPV(Number(value)));
  };

  const updateValueVCSPV = (value: number | "") => {
    dispatch(updateVCSPV(Number(value)));
  };

  return (
    <div className="earnings-calculator__progress-tile">
      {/** Progress tile component here */}
      <div className="earnings-calculator__progress-tile__reset-button">
        <span className="earnings-calculator__progress-tile__reset-text">{`${t(
          "progressTile.resetValues",
          defaultContent["progressTile"]["resetValues"],
        )}`}</span>
        <LeafLinkButton
          onClick={handleReset}
          label={`${t("progressTile.resetValues", defaultContent["progressTile"]["resetValues"])}`}
          as={"link"}
        >
          <IconResetCircle disabled={isDisabled} />
        </LeafLinkButton>
      </div>
      <div className="earnings-calculator__progress-tile__container">
        {/* PV */}
        <div className="earnings-calculator__progress-tile__content">
          <div className="earnings-calculator__progress-tile__title">
            {`${t("keyMetrics.personalPv", defaultContent["keyMetrics"]["personalPv"])}`}
          </div>
          <div className="earnings-calculator__progress-tile__description">
            {`${t("progressTile.description", defaultContent["progressTile"]["description"])}`}
            <LeafRectangleLoader isLoading={isLoading} height="1rem" width="3rem">
              <span className="earnings-calculator__progress-tile__description-pv">
                {`${t("progressTile.pv", defaultContent["progressTile"]["pv"], {
                  pv: CurrentPersonalPV.Formatted,
                })}`}
              </span>
            </LeafRectangleLoader>
          </div>
          <EarningsCalculatorInput
            countryCode={Settings.BonusSchedule.ISOCountry}
            min={Settings.PersonalPV.Min}
            max={Settings.PersonalPV.Max}
            base={CurrentPersonalPV.Actual}
            marks={Settings.PersonalPV.Marks}
            value={PersonalPV}
            onChange={updateValuePersonalPV}
            disabled={CurrentPersonalPV.Actual === Settings.PersonalPV.Max || disableInput}
            placeholder={`${t("progressTile.inputPlaceholder", defaultContent["progressTile"]["inputPlaceholder"])}`}
            suffix={`${t("progressTile.pvSuffix", defaultContent["progressTile"]["pvSuffix"])}`}
            isLoading={isLoading}
            ariaLabel={`${t("keyMetrics.personalPv", defaultContent["keyMetrics"]["personalPv"])}`}
          />
        </div>
        {/* VCS PV */}
        {isCSIActive && (
          <div className="earnings-calculator__progress-tile__content">
            <div className="earnings-calculator__progress-tile__title">{`${t(
              "keyMetrics.vcsPv",
              defaultContent["keyMetrics"]["vcsPv"],
            )}`}</div>
            <div className="earnings-calculator__progress-tile__description">
              {`${t("progressTile.description", defaultContent["progressTile"]["description"])}`}
              <span className="earnings-calculator__progress-tile__description-pv">
                <LeafRectangleLoader isLoading={isLoading} height="1rem" width="3rem">
                  {`${t("progressTile.pv", defaultContent["progressTile"]["pv"], {
                    pv: CurrentVCSPV.Formatted,
                  })}`}
                </LeafRectangleLoader>
              </span>
            </div>
            <EarningsCalculatorInput
              disabled={CurrentVCSPV.Actual === Settings.CSI.GPV.Max || disableInput}
              countryCode={Settings.BonusSchedule.ISOCountry}
              min={Settings.CSI.GPV.Min}
              max={Settings.CSI.GPV.Max}
              base={CurrentVCSPV.Actual}
              marks={Settings.CSI.GPV.Marks}
              value={VCSPV}
              onChange={updateValueVCSPV}
              placeholder={`${t("progressTile.inputPlaceholder", defaultContent["progressTile"]["inputPlaceholder"])}`}
              suffix={`${t("progressTile.vcsPvSuffix", defaultContent["progressTile"]["vcsPvSuffix"])}`}
              isLoading={isLoading}
              ariaLabel={`${t("keyMetrics.vcsPv", defaultContent["keyMetrics"]["vcsPv"])}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EarningsCalculatorProgressTile;
